import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Users, Send, Sparkles, ShieldCheck } from 'lucide-react';
import { useToast } from '../../../Utils/toastContext';
import TemplateViewer from './template-viewer';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { debounce } from 'lodash';

const BASE_URL = process.env.REACT_APP_BASE_URL;
export default function AgentContractReassign() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [contract, setContract] = useState(null);
  const [users, setUsers] = useState(null);
  const { customerId, transactionId, contractId } = useParams();
  const [loading, setLoading] = useState(false);
  const [fieldAssignments, setFieldAssignments] = useState([]);
  const [fields, setFields] = useState([]);
  const GetAPIcalls = useCallback(
    async (url, isBlob = false) => {
      const token = localStorage.getItem('jwtToken');
      const apiUrl = `${BASE_URL}${url}`;

      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired.');
        }

        const errorData = await response.json().catch(() => ({
          message: 'Unexpected error',
        }));

        throw new Error(errorData.message);
      }

      return isBlob ? response.blob() : response.json();
    },
    [navigate],
  );

  const postSendContractAPI = async (payload) => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const apiUrl = `${BASE_URL}/api/v1/agent/customers/${customerId}/client-deals/${transactionId}/contracts/${contractId}/reassign`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));

        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const sendContract = useCallback(
    debounce(async () => {
      const unassigned = fields.filter(
        (field) =>
          field.required &&
          (!field.assignedUsers || field.assignedUsers.length === 0),
      );

      if (unassigned.length > 0) {
        const details = unassigned.map(
          (field) => `${field.label || field.type} (Page ${field.pageNumber})`,
        );

        showToast(
          `Please assign all required fields:\n${details.join(', ')}`,
          'error',
        );

        return;
      }

      setLoading(true);

      const payload = {
        field_assignments: fields.map((field) => ({
          field_id: field.id,
          field_type: field.type,
          label: field.label,
          assigned_users: field.assignedUsers.map((user) => ({
            user_id: user.user_id,
            role: user.role,
            name: user.name,
          })),
        })),
      };

      try {
        const response = await postSendContractAPI(payload);

        if (response.status === 200) {
          showToast('Contract sent successfully', 'success');
          navigate(-1);
        }
      } catch (error) {
        showToast(error.message, 'error');
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 500);
      }
    }, 500),
    [fields, contractId, customerId, transactionId],
  );
  const extractFields = (document, assignments = []) => {
    const fields = [];

    document.pages.forEach((page, pageIndex) => {
      const traverse = (node) => {
        if (
          [
            'placeholder',
            'signature',
            'dateField',
            'checkbox',
            'radioGroup',
          ].includes(node.type)
        ) {
          const fieldId = node.attrs.id;
          console.log('asignments', assignments);
          // Find existing assignment from API
          const existingAssignment = assignments.find(
            (item) => item.id === fieldId,
          );

          fields.push({
            id: fieldId,
            type: node.type,
            pageId: page.id,
            pageNumber: pageIndex + 1,
            label: node.attrs.label || node.attrs.key || node.type,

            assignedUsers:
              existingAssignment?.assignedUsers ||
              node.attrs.assignedUsers ||
              [],

            required: true,
          });
        }

        if (node.content) {
          node.content.forEach(traverse);
        }
      };

      traverse(page.content);
    });

    return fields;
  };
  useEffect(() => {
    if (contract && contract.document_content && fieldAssignments.length) {
      const extracted = extractFields(
        contract.document_content,
        fieldAssignments,
      );

      setFields(extracted);
    }
  }, [contract, fieldAssignments]);
  const getContractDetails = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/agent/customers/${customerId}/client-deals/${transactionId}/contracts/${contractId}`,
      );

      if (response.status === 200) {
        const contractData = response.data.contract;

        setContract(contractData);

        // Save existing assignments
        const normalizedFields = contractData.field_assignments.map((f) => ({
          id: f.field_id,
          label: f.label,
          type: f.field_type,
          pageNumber: f.page,

          // ✅ convert object → array
          assignedUsers: f.assigned_user ? [f.assigned_user] : [],
        }));
        console.log('normalised fields', normalizedFields);
        setFieldAssignments(normalizedFields || []);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }

        if (response.status === 400) {
          showToast(response.message, 'error');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  const getUsers = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/agent/customers/${customerId}/client-deals/${transactionId}/contracts/available-signers`,
      );

      if (response.status == 200) {
        setUsers(response.data.signers);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) {
          showToast(response.message, 'error');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  useEffect(() => {
    getContractDetails();
    getUsers();
  }, []);
  return (
    <div
      className="
  min-h-screen
  bg-[radial-gradient(circle_at_top_left,_rgba(55,90,231,0.18),_transparent_35%),
  linear-gradient(135deg,#f8fbff_0%,#ffffff_45%,#eef4ff_100%)]
  "
    >
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <main
        className="
    mx-auto
    flex
    w-full
    flex-col
    gap-6
    px-3
    py-5
    sm:px-6
    lg:px-10
    "
      >
        <section
          className="
      overflow-hidden
      rounded-[32px]
      border
      border-slate-200/80
      bg-white/80
      shadow-[0_30px_90px_-35px_rgba(55,90,231,0.35)]
      backdrop-blur-xl
      "
        >
          {/* HEADER */}

          <div
            className="
        relative
        overflow-hidden
        bg-gradient-to-r
        from-[#375AE7]
        via-[#4F6FF5]
        to-[#7C3AED]
        px-5
        py-6
        text-white
        sm:px-8
        "
          >
            <div
              className="
          absolute
          right-0
          top-0
          h-40
          w-40
          rounded-full
          bg-white/10
          blur-3xl
          "
            />

            <div
              className="
          relative
          flex
          flex-col
          gap-5
          lg:flex-row
          lg:items-center
          lg:justify-between
          "
            >
              {/* TITLE */}

              <div
                className="
            flex
            items-start
            gap-4
            "
              >
                <div
                  className="
              flex
              h-14
              w-14
              shrink-0
              items-center
              justify-center
              rounded-2xl
              bg-white/20
              shadow-lg
              backdrop-blur
              "
                >
                  <FileText size={28} />
                </div>

                <div>
                  <div
                    className="
                flex
                items-center
                gap-2
                "
                  >
                    <Sparkles size={16} />

                    <p
                      className="
                  text-xs
                  font-semibold
                  uppercase
                  tracking-[0.25em]
                  text-blue-100
                  "
                    >
                      Contract Workflow
                    </p>
                  </div>

                  <h1
                    className="
                mt-2
                text-xl
                font-bold
                sm:text-2xl
                "
                  >
                    Review & Assign Contract Fields
                  </h1>

                  <p
                    className="
                mt-2
                max-w-xl
                text-sm
                leading-relaxed
                text-blue-100
                "
                  >
                    Review the contract details, assign required placeholders to
                    customers or team members, and send the document for
                    completion.
                  </p>
                </div>
              </div>

              {/* STATUS CARD */}

              <div
                className="
            flex
            items-center
            gap-3
            rounded-2xl
            border
            border-white/20
            bg-white/10
            px-4
            py-3
            backdrop-blur-md
            "
              >
                <div
                  className="
              flex
              h-10
              w-10
              items-center
              justify-center
              rounded-xl
              bg-white/20
              "
                >
                  <ShieldCheck size={22} />
                </div>

                <div>
                  <p
                    className="
                text-xs
                text-blue-100
                "
                  >
                    Secure Document Flow
                  </p>

                  <p
                    className="
                text-sm
                font-semibold
                "
                  >
                    Ready for Assignment
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* STEPS */}

          <div
            className="
        grid
        gap-4
        p-5
        sm:grid-cols-3
        "
          >
            {/* STEP 1 */}

            <div
              className="
          flex
          items-center
          gap-4
          rounded-2xl
          border
          border-blue-100
          bg-blue-50/60
          p-4
          "
            >
              <div
                className="
            flex
            h-11
            w-11
            items-center
            justify-center
            rounded-xl
            bg-[#375AE7]
            text-white
            "
              >
                <FileText size={22} />
              </div>

              <div>
                <p className="text-sm font-semibold text-slate-800">
                  Review Contract
                </p>

                <p className="text-xs text-slate-500">
                  Verify clauses and details
                </p>
              </div>
            </div>

            {/* STEP 2 */}

            <div
              className="
          flex
          items-center
          gap-4
          rounded-2xl
          border
          border-purple-100
          bg-purple-50/60
          p-4
          "
            >
              <div
                className="
            flex
            h-11
            w-11
            items-center
            justify-center
            rounded-xl
            bg-purple-500
            text-white
            "
              >
                <Users size={22} />
              </div>

              <div>
                <p className="text-sm font-semibold text-slate-800">
                  Assign Fields
                </p>

                <p className="text-xs text-slate-500">
                  Select users for placeholders
                </p>
              </div>
            </div>

            {/* STEP 3 */}

            <div
              className="
          flex
          items-center
          gap-4
          rounded-2xl
          border
          border-green-100
          bg-green-50/60
          p-4
          "
            >
              <div
                className="
            flex
            h-11
            w-11
            items-center
            justify-center
            rounded-xl
            bg-emerald-500
            text-white
            "
              >
                <Send size={22} />
              </div>

              <div>
                <p className="text-sm font-semibold text-slate-800">
                  Send For Completion
                </p>

                <p className="text-xs text-slate-500">
                  Request required information
                </p>
              </div>
            </div>
          </div>

          {/* VIEWER */}

          <div
            className="
        border-t
        border-slate-200
        bg-slate-50/50
        p-3
        sm:p-5
        "
          >
            <TemplateViewer
              contract={contract?.document_content}
              fields={fields}

              setFields={setFields}
              users={users}
              onSend={sendContract}
            />
          </div>
        </section>
      </main>
    </div>
  );
}
